import type { CheckoutGolfService, GolfServiceLocation, GolfServiceOption, GolfServiceSelectedValue } from '@/scopes/checkout/domain/types'

/** Returns all available golf course locations from the service */
export function golfLocationsSelector(service: CheckoutGolfService | null): GolfServiceLocation[] {
  if (!service)
    return []
  return Object.values(service.locations)
}

/** Returns available hole counts for the selected location */
export function golfHolesForLocationSelector(
  service: CheckoutGolfService | null,
  locationId: string | null,
): number[] {
  if (!service || !locationId)
    return []
  return service.locations[locationId]?.availableHoles ?? []
}

/** Returns available dates for the given location and hole count (sorted ascending) */
export function golfDatesForFiltersSelector(
  service: CheckoutGolfService | null,
  locationId: string | null,
  holes: number | null,
): string[] {
  if (!service || !locationId)
    return []
  return Array.from(
    new Set(
      Object.values(service.options)
        .filter(o => o.locationId === locationId && (holes === null || o.holes === holes))
        .map(o => o.date),
    ),
  ).sort()
}

/** Returns available options matching all current filter values */
export function golfOptionsForFiltersSelector(
  service: CheckoutGolfService | null,
  locationId: string | null,
  holes: number | null,
  date: string | null,
  withBuggy: boolean | null,
): GolfServiceOption[] {
  if (!service)
    return []
  return Object.values(service.options).filter((o) => {
    if (locationId && o.locationId !== locationId)
      return false
    if (holes !== null && o.holes !== holes)
      return false
    if (date && o.date !== date)
      return false
    if (withBuggy !== null && o.withBuggy !== withBuggy)
      return false
    return true
  })
}

/** Returns whether the given location offers a buggy option */
export function golfLocationIsBuggyEligibleSelector(
  service: CheckoutGolfService | null,
  locationId: string | null,
): boolean {
  if (!service || !locationId)
    return false
  return service.locations[locationId]?.isBuggyEligible ?? false
}

/** Returns the current editing state for a given slot */
export function golfEditingValueForSlotSelector(
  service: CheckoutGolfService | null,
  slotId: string | null,
): GolfServiceSelectedValue | undefined {
  if (!service || !slotId)
    return undefined
  return service.editingSelectedValues[slotId]
}

/** Returns the price and buggy flag of the currently saved option for a slot, or null if no option is selected */
export function golfSlotPriceSelector(
  service: CheckoutGolfService | null,
  slotId: string,
): { price: number, withBuggy: boolean } | null {
  if (!service)
    return null
  const slot = service.selectedValues[slotId]
  if (!slot?.optionId)
    return null
  const option = service.options[slot.optionId]
  if (!option)
    return null
  const calculatedPrice = option.withBuggy ? option.price : option.price * Math.max(1, slot.participantIds.length)

  return { price: calculatedPrice, withBuggy: option.withBuggy }
}

const STARTING_HOUR = 8
const ENDING_HOUR = 16
const MINUTES_INTERVAL = 30

export function golfTimesSelector(service: CheckoutGolfService | null): string[] {
  if (!service)
    return []
  const times: string[] = []
  for (let hour = STARTING_HOUR; hour <= ENDING_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += MINUTES_INTERVAL) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      times.push(time)
    }
  }
  return times
}
